import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { GraphCommand } from "./canvas-commands";
import type { EditToken } from "./canvas-history";
import type { CanvasHistoryController } from "./use-canvas-history";

export interface CanvasEditability {
  canEditDocument: boolean;
  disabledReason: string;
  generation: number;
  canUndo: boolean;
  canRedo: boolean;
  isDocumentEditable: () => boolean;
  runDocumentMutation: (action: () => void) => boolean;
  execute: (command: GraphCommand, token?: EditToken) => void;
  begin: (kind: string) => EditToken | null;
  finish: (token: EditToken | null) => void;
  isSessionActive: (token: EditToken | null) => boolean;
  undo: () => void;
  redo: () => void;
}
const deny = () => false;
const noop = () => {};
const CanvasEditabilityContext = createContext<CanvasEditability>({
  canEditDocument: false, disabledReason: "This diagram is read-only.", generation: 0,
  canUndo: false, canRedo: false, isDocumentEditable: deny, runDocumentMutation: deny,
  execute: noop, begin: () => null, finish: noop, isSessionActive: deny, undo: noop, redo: noop,
});
export const CanvasEditabilityProvider = CanvasEditabilityContext.Provider;
export const useCanvasEditability = () => useContext(CanvasEditabilityContext);

export function useCanvasMutationGate(options: {
  canEditDocument: boolean;
  disabledReason?: string;
  isDocumentEditable?: () => boolean;
  history: CanvasHistoryController;
  onReplayRejected?: () => void;
}): CanvasEditability {
  const current = useRef(options);
  current.current = options;
  const mounted = useRef(true);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isDocumentEditable = useCallback(() => mounted.current && current.current.canEditDocument &&
    (current.current.isDocumentEditable?.() ?? true), []);
  const wasEditable = useRef(options.canEditDocument);
  useLayoutEffect(() => {
    if (wasEditable.current && !options.canEditDocument) options.history.closeAuthority();
    wasEditable.current = options.canEditDocument;
  }, [options.canEditDocument, options.history.closeAuthority]);
  const generation = options.history.state.epoch;
  const commands = useMemo(() => {
    const allowed = () => isDocumentEditable() && current.current.history.current.current.epoch === generation;
    return {
      execute: (command: GraphCommand, token?: EditToken) => { if (allowed()) current.current.history.execute(command, token); },
      begin: (kind: string) => allowed() ? current.current.history.begin(kind) : null,
      finish: (token: EditToken | null) => { if (allowed() && token) current.current.history.finish(token); },
      undo: () => { if (allowed() && current.current.history.undo().historyCleared) current.current.onReplayRejected?.(); },
      redo: () => { if (allowed() && current.current.history.redo().historyCleared) current.current.onReplayRejected?.(); },
      runDocumentMutation: (action: () => void) => { if (!allowed()) return false; action(); return true; },
    };
  }, [generation, isDocumentEditable]);
  return useMemo(() => ({ ...commands, canEditDocument: options.canEditDocument,
    disabledReason: options.disabledReason ?? "This diagram is read-only.", generation,
    canUndo: options.canEditDocument && options.history.canUndo,
    canRedo: options.canEditDocument && options.history.canRedo,
    isDocumentEditable, isSessionActive: options.history.isSessionActive }),
  [commands, options.canEditDocument, options.disabledReason, generation, options.history.canUndo,
    options.history.canRedo, isDocumentEditable, options.history.isSessionActive]);
}

// Each rendered commit closes over its own token. Old DOM/portal handlers cannot
// acquire a new session after replay, checkpoint adoption or permission changes.
export function useCanvasEditSession(kind: string) {
  const gate = useCanvasEditability();
  const [token, setToken] = useState<EditToken | null>(null);
  const active = gate.canEditDocument && gate.isSessionActive(token);
  const begin = () => {
    if (gate.isSessionActive(token)) return token;
    const next = gate.begin(kind); setToken(next); return next;
  };
  const update = (command: GraphCommand) => { if (token) gate.execute(command, token); };
  const finish = () => { gate.finish(token); setToken(null); };
  return { active, begin, update, finish };
}

export function useCanvasResizeSession() {
  const { generation, begin, finish, isDocumentEditable, isSessionActive } = useCanvasEditability();
  return useMemo(() => {
    let token: EditToken | null = null;
    return {
      onResizeStart: () => { token = null; },
      onResizeEnd: () => { finish(token); token = null; },
      shouldResize: () => {
        if (!isDocumentEditable()) return false;
        // React Flow omits onResizeEnd after a handle click without movement.
        if (!token) token = begin("resize");
        return isSessionActive(token);
      },
    };
  }, [generation, begin, finish, isDocumentEditable, isSessionActive]);
}
